<script setup>
import Dialog from 'primevue/dialog'
import Calendar from 'primevue/calendar'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import MultiSelect from 'primevue/multiselect'
import axios from 'axios'
import SendReportsDialog from './send_reports_dialog.vue'

const props = defineProps({
  postCsvUrl: { type: String, default: null },
  creatorsUrl: { type: String, default: null },
  departmentId: { type: Number, default: null },
  actualPeriodId: { type: String, default: null },
  actualPeriodStartDate: { type: String, default: null },
  monthlyActualReports: {
    type: Array,
    default: () => {
      return []
    }
  }
})

// UI管理フラグ
const isLoading = ref(false)
const displayModal = ref(false)
const displayResult = ref(false)
const checkedList = ref([])

const creators = ref([])
const minDate = new Date(props.actualPeriodStartDate)
const maxDate = new Date(props.actualPeriodStartDate)
maxDate.setMonth(minDate.getMonth() + 1)
const formData = reactive({
  file: null,
  dates: [],
  creators: []
})
const errorMessage = ref('')
const result = ref([])

const calendar = ref(null)
// 出てくるカレンダーの月を作業明細月に変更
function setupCalendar() {
  calendar.value.currentMonth = new Date(props.actualPeriodStartDate).getMonth()
  calendar.value.currentYear = new Date(
    props.actualPeriodStartDate
  ).getFullYear()
}

// インポートボタン押せる条件
const isSubmitActive = computed(() => {
  if (formData.file == null) return false
  if (formData.dates.length !== 2) return false
  if (formData.creators.length === 0) return false

  return true
})

const selectedMonthlyActualReports = computed(() => {
  return props.monthlyActualReports.filter((report) =>
    checkedList.value.includes(report.id)
  )
})

onMounted(async () => {
  addEventListener('rowSelected', updateCheckedList)
  axios.defaults.headers.common['X-CSRF-Token'] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content')
  await fetchCreators()
})
onBeforeUnmount(() => {
  removeEventListener('rowSelected', updateCheckedList)
})

function updateCheckedList() {
  const table = document.getElementById('monthly-actual-reports')
  const checkboxes = table.querySelectorAll('input[type="checkbox"]')
  checkedList.value = []

  checkboxes.forEach((checkbox) => {
    if (checkbox.checked) {
      const row = checkbox.closest('tr')
      const id = Number(row.getAttribute('data-id'))
      checkedList.value.push(id)
    }
  })
}

async function fetchCreators() {
  const result = await axios.get(props.creatorsUrl)
  creators.value = result.data
}

async function submit() {
  const url = props.postCsvUrl
  const data = new FormData()
  data.append('file', formData.file)
  data.append('first_date', formData.dates[0])
  // 1日だけ選んだ場合datesは[選んだ日, null] になっている
  data.append('last_date', formData.dates[1] || formData.dates[0])
  formData.creators.forEach((creator) => {
    data.append('grandit_cds[]', creator.grandit_cd)
  })
  const headers = { 'content-type': 'multipart/form-data' }
  isLoading.value = true
  try {
    const res = await axios.post(url, data, { headers })
    isLoading.value = false
    displayResult.value = true
    result.value = res.data.actual_details
    errorMessage.value = res.data.errors
  } catch (error) {
    console.error('リクエストでエラーが発生しました:', error)
    errorMessage.value = 'サーバーエラーが発生しました。'
    isLoading.value = false
  }
}

function reload() {
  location.reload()
}

function showImportModal() {
  displayModal.value = true
}

function moveToImportHistory() {
  location.href = `/actual_periods/${props.actualPeriodId}/actual_report_imports`
}

function handleFileChange(event) {
  const file = event.target.files[0]

  if (file) {
    formData.file = file
  } else {
    formData.file = null
  }
}

// 結果画面で上のボタン押して閉じた時もリロードする
function onHide() {
  if (displayResult.value) {
    reload()
  }
}
</script>

<template>
  <div class="flex flex-row gap-2">
    <button
      class="group flex items-center justify-center rounded-md border border-gray-500 bg-white py-2 pl-3 pr-4 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
      @click="moveToImportHistory"
    >
      <div class="flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="mr-1 h-5 w-5 text-gray-500 group-hover:text-white"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
          />
        </svg>
        実績CSVインポート履歴
      </div>
    </button>
    <button
      class="group flex items-center justify-center rounded-md border border-gray-500 bg-white py-2 pl-3 pr-4 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-green-500 hover:text-white sm:w-auto"
      @click="showImportModal"
    >
      <div class="flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="h-5 w-5 pr-1 text-gray-500 group-hover:text-white"
        >
          <path
            fill-rule="evenodd"
            d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z"
            clip-rule="evenodd"
          />
        </svg>
        実績CSVインポート
      </div>
    </button>
    <SendReportsDialog
      :checked-list="checkedList"
      :selected-monthly-actual-reports="selectedMonthlyActualReports"
      :url="`/actual_periods/${props.actualPeriodId}/send_reports`"
    ></SendReportsDialog>
    <Dialog
      v-model:visible="displayModal"
      header="実績CSVインポート"
      :modal="true"
      :min-date="minDate"
      :max-date="maxDate"
      :style="{ width: '50rem' }"
      @hide="onHide"
    >
      <vue-element-loading :active="isLoading" />
      <div v-if="!displayResult">
        <div
          v-if="errorMessage"
          id="errors"
          class="mb-2 mt-2 rounded-md bg-red-50 p-4"
        >
          <div class="mt-2 text-sm text-red-700">エラー:{{ errorMessage }}</div>
        </div>
        <span class="text-gray-800" style="font-size: 0.75rem">タブ区切り、文字コードSHIFT-JISのCSVファイルを選択してください。</span>
        <div class="mb-2">
          <input type="file" accept=".csv, .txt" @change="handleFileChange" />
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >期間</span
          >
          <Calendar
            ref="calendar"
            v-model="formData.dates"
            selection-mode="range"
            :manual-input="false"
            :input-style="{ width: '15rem' }"
            class="focus:outline-blue string mt-2 h-10 leading-normal"
            @show="setupCalendar"
          />
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >インポート対象者</span
          >
          <MultiSelect
            v-model="formData.creators"
            :options="creators"
            option-label="name"
            filter
            scroll-height="400px"
            placeholder="クリエイター選択"
            class="h-10 rounded border text-sm"
          />
        </div>
      </div>
      <div v-if="displayResult">
        <div
          v-if="errorMessage"
          id="errors"
          class="mb-2 mt-2 rounded-md bg-red-50 p-4"
        >
          <div class="mt-2 text-sm text-red-700">エラー:{{ errorMessage }}</div>
        </div>
        <div class="mb-2">作業実績を登録しました</div>
        <DataTable :value="result" style="font-size: 0.75rem">
          <Column field="title" header="案件名"></Column>
          <Column field="work_date" header="作業日"></Column>
          <Column field="creator.name" header="作業者"></Column>
        </DataTable>
      </div>
      <template #footer>
        <button
          v-if="!displayResult"
          :disabled="!isSubmitActive"
          class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
          :class="{ 'cursor-not-allowed opacity-50': !isSubmitActive }"
          @click="submit"
        >
          インポート
        </button>
        <button
          v-if="displayResult"
          class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
          @click="reload"
        >
          OK
        </button>
      </template>
    </Dialog>
  </div>
</template>
